<template>
    <div class="d-flex align-items-center">
        <b-button
            :title="addNew ? 'Generate Door Code' : 'Change Door Code'"
            variant="outline-primary"
            @click="$bvModal.show(doorCodeModalId)"
        >
            <font-awesome-icon :icon="['fas', addNew ? 'plus' : 'edit']" />
        </b-button>
        <validation-observer ref="delete_form" v-slot="{ meta, handleSubmit }">
            <modal
                v-if="defaultProfileUser"
                :id="doorCodeModalId"
                title="Door Code"
                :ok-title="addNew ? 'Generate' : 'Change'"
                cancel-title="Close"
                @ok.prevent="createUpdateKabaDoorCode"
                :ok-disabled="!meta.valid"
                :scrollable="false"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-form @submit.prevent="handleSubmit(_, createUpdateDoorCode)">
                    <b-row>
                        <b-col>
                            <span>Kaba Profile:</span>
                            <form-select
                                v-model="profileUser"
                                class="w-100 mt-2 mb-2"
                                :options="profileUserOptions"
                            ></form-select>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <b-alert v-if="!addNew" class="mt-4" show variant="warning">
                                This action will generate a new lock code for the guest.
                                Please make sure the guest is made aware of the new lock code.
                            </b-alert>
                        </b-col>
                    </b-row>
                </b-form>
            </modal>
            <modal
                v-else
                :id="doorCodeModalId"
                title="Door Code"
                :ok-title="addNew ? 'Generate' : 'Change'"
                cancel-title="Close"
                @ok.prevent="createUpdateDoorCode"
                :ok-disabled="!meta.valid"
                :scrollable="false"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-form @submit.prevent="handleSubmit(_, createUpdateDoorCode)">
                    <b-row>
                        <b-col>
                            <span>Door Code Begins:</span>
                            <b-timepicker
                                class="mt-2 mb-2"
                                v-model="fromDate"
                                :hour12="twelveHourFormat"
                                close-button-variant="outline-primary"
                                minutes-step="5"
                            />
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <span>Door Code Ends:</span>
                            <b-timepicker
                                class="mt-2 mb-2"
                                v-model="toDate"
                                :hour12="twelveHourFormat"
                                close-button-variant="outline-primary"
                                minutes-step="5"
                            />
                        </b-col>
                    </b-row>
                </b-form>
            </modal>
        </validation-observer>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../../../api.jsx';
import Modal from '../../../../generic/modal/modal.vue';
import BusyModal from '../../../../generic/busy-modal/busy-modal.vue';
import FormSelect from '../../../../generic/form-select/form-select.vue';
import {
    GUEST_DOOR_CODE_ENDPOINT,
    GUEST_DOOR_CODE_PROFILE_USERS_ENDPOINT,
    GUEST_DOOR_CODE_KABA_ENDPOINT
} from '../../../../../endpoints.js';
import { formatResponseError } from '../../../../../utils/api';

export default {
    components: { Modal, BusyModal, FormSelect },
    data() {
        return {
            loading: false,
            doorCodeModalId: null,
            fromDate: '',
            toDate: '',
            defaultProfileUser: null,
            profileUser: null,
            profileUserOptions: null,
        };
    },
    inject: ['showSuccessToast', 'showErrorToast'],
    created() {
        this.doorCodeModalId = `door-code-modalId-${this.params.data.id}`;
        this.fromDate = this.params.data.dt_from == null ? this.reservation.check_in : this.params.data.dt_from;
        this.toDate = this.params.data.dt_to == null ? this.reservation.check_out : this.params.data.dt_to;
        this.defaultProfileUser = this.params.data.user_id || null;
        if (this.defaultProfileUser) {
            this.profileUser = this.defaultProfileUser;
            this.fetchProfileUsers();
        }
    },
    computed: {
        ...mapGetters('guest', ['metadata', 'reservation']),
        ...mapGetters('header', ['userTimeFormat']),
        twelveHourFormat() {
            return this.userTimeFormat === 'hh:mm a';
        },
        addNew() {
            return this.params.data.code == '';
        },
    },
    methods: {
        async createUpdateKabaDoorCode() {
            this.loading = true;
            try {
                await API.post(GUEST_DOOR_CODE_KABA_ENDPOINT(), {
                    kaba_profile_user_id: this.profileUser,
                    reservation_id: this.params.data.id,
                });
                this.$bvModal.hide(this.doorCodeModalId);
                this.params.refreshReservation(() =>
                    this.showSuccessToast({ text: `Door code has been ${this.addNew ? 'created' : 'updated'}.` })
                );
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.showErrorToast({
                    text: generic || `Could not ${this.addNew ? 'create' : 'update'} door code.`,
                });
            } finally {
                this.loading = false;
            }
        },
        async createUpdateDoorCode() {
            this.loading = true;
            try {
                await API.post(GUEST_DOOR_CODE_ENDPOINT(), {
                    start_access_time: this.params.data.from_date + 'T' + this.fromDate,
                    end_access_time: this.params.data.to_date + 'T' + this.toDate,
                    reservation_id: this.params.data.id,
                });
                this.$bvModal.hide(this.doorCodeModalId);
                this.params.refreshReservation(() =>
                    this.showSuccessToast({ text: `Door code has been ${this.addNew ? 'created' : 'updated'}.` })
                );
            } catch (err) {
                const { generic } = formatResponseError(err);
                this.showErrorToast({
                    text: generic || `Could not ${this.addNew ? 'create' : 'update'} door code.`,
                });
            } finally {
                this.loading = false;
            }
        },
        async fetchProfileUsers() {
            this.loading = true;
            try {
                const { data } = await API.get(GUEST_DOOR_CODE_PROFILE_USERS_ENDPOINT());
                this.profileUserOptions = data.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
            } catch (e) {
                this.errorToast({
                    text: 'Could not fetch profiles.',
                });
            } finally {
                this.loading = false;
            }
        }
    },
};
</script>
